import { useState } from "react";
import { motion } from "framer-motion";

const MAQUITO_URL = "https://customer-assets.emergentagent.com/job_maquito-helper/artifacts/tfyykh0b_chat.svg";

export default function LeadForm({ onSubmit, onBypass }) {
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    celular: "",
    empresa: ""
  });
  
  const [touched, setTouched] = useState({
    nombre: false,
    email: false,
    celular: false
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [devInput, setDevInput] = useState("");

  // Validation functions
  const validateName = (name) => {
    return name.trim().length >= 3;
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Remove spaces and special characters for validation
    const cleanPhone = phone.replace(/[\s\-\(\)\.]/g, "");
    // Chilean format: 9 digits starting with 9, or with +56
    const phoneRegex = /^(\+?56)?9?\d{8,9}$/;
    return phoneRegex.test(cleanPhone) && cleanPhone.length >= 8;
  };

  const getFieldStatus = (field) => {
    const value = formData[field];
    const isTouched = touched[field] || submitted;
    
    if (!isTouched) return "neutral";
    
    switch (field) {
      case "nombre":
        return validateName(value) ? "valid" : "invalid";
      case "email":
        return validateEmail(value) ? "valid" : "invalid";
      case "celular":
        return validatePhone(value) ? "valid" : "invalid";
      default:
        return "neutral";
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    
    const isValid = 
      validateName(formData.nombre) && 
      validateEmail(formData.email) && 
      validatePhone(formData.celular);
    
    if (isValid) {
      onSubmit(formData);
    }
  };

  const handleDevInputChange = (e) => {
    const value = e.target.value.toLowerCase().trim();
    setDevInput(value);
  };

  const handleDevInputKeyDown = (e) => {
    if (e.key === "Enter") {
      const value = devInput.toLowerCase().trim();
      if (["mkt", "qa", "test"].includes(value)) {
        onBypass();
      }
    }
  };

  const getInputClasses = (field) => {
    const status = getFieldStatus(field);
    const baseClasses = "w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 outline-none text-slate-700 placeholder-slate-400";
    
    switch (status) {
      case "valid":
        return `${baseClasses} border-green-500 bg-green-50 focus:border-green-600 focus:ring-2 focus:ring-green-200`;
      case "invalid":
        return `${baseClasses} border-red-500 bg-red-50 focus:border-red-600 focus:ring-2 focus:ring-red-200`;
      default:
        return `${baseClasses} border-slate-300 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200`;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="flex gap-3"
      data-testid="lead-form-container"
    >
      <img 
        src={MAQUITO_URL} 
        alt="Maquito" 
        style={{ width: '71px', height: 'auto', objectFit: 'contain' }}
        className="flex-shrink-0"
      />
      <div className="bg-green-100 rounded-2xl px-5 py-4 max-w-md w-full">
        <p className="text-sm text-slate-700 mb-4 leading-relaxed">
          ¡Hola! Soy <strong>Maquito</strong> de Taglermaq 🤖
          <br /><br />
          Para entregarte una experiencia más rápida y precisa, necesito que completes los siguientes datos <strong>obligatorios</strong>:
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nombre field */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Nombre y apellido
            </label>
            <input
              type="text"
              value={formData.nombre}
              onChange={(e) => handleChange("nombre", e.target.value)}
              onBlur={() => handleBlur("nombre")}
              placeholder="Tu nombre completo"
              className={getInputClasses("nombre")}
              data-testid="lead-form-nombre"
            />
            {getFieldStatus("nombre") === "invalid" && (
              <p className="text-red-500 text-sm mt-1 italic">
                Rellena este campo, por favor.
              </p>
            )}
          </div>

          {/* Email field */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Correo electrónico
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              onBlur={() => handleBlur("email")}
              placeholder="tu@correo.com"
              className={getInputClasses("email")}
              data-testid="lead-form-email"
            />
            {getFieldStatus("email") === "invalid" && (
              <p className="text-red-500 text-sm mt-1 italic">
                {formData.email.length > 0 ? "Ingresa un correo válido, por favor." : "Rellena este campo, por favor."}
              </p>
            )}
          </div>

          {/* Celular field */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Número de celular
            </label>
            <input
              type="tel"
              value={formData.celular}
              onChange={(e) => handleChange("celular", e.target.value)}
              onBlur={() => handleBlur("celular")}
              placeholder="+56 9 1234 5678"
              className={getInputClasses("celular")}
              data-testid="lead-form-celular"
            />
            {getFieldStatus("celular") === "invalid" && (
              <p className="text-red-500 text-sm mt-1 italic">
                {formData.celular.length > 0 ? "Ingresa un número válido, por favor." : "Rellena este campo, por favor."}
              </p>
            )}
          </div>

          {/* Empresa field (opcional) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Empresa
            </label>
            <input
              type="text"
              value={formData.empresa}
              onChange={(e) => handleChange("empresa", e.target.value)}
              placeholder=""
              className="w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 outline-none text-slate-700 placeholder-slate-400 border-slate-300 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200"
              data-testid="lead-form-empresa"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-xl transition-colors duration-200 flex items-center justify-center gap-2"
            data-testid="lead-form-submit"
          >
            <span>Continuar</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </form>
        
        <p className="text-xs text-slate-500 mt-3 text-center">
          Tus datos están seguros con nosotros 🔒
        </p>

        {/* Developer bypass input */}
        <div className="mt-4 pt-3 border-t border-green-200">
          <input
            type="text"
            value={devInput}
            onChange={handleDevInputChange}
            onKeyDown={handleDevInputKeyDown}
            placeholder="Código de acceso sólo para desarrolladores"
            className="w-full px-3 py-2 text-xs rounded-lg border border-slate-200 bg-slate-50 text-slate-500 placeholder-slate-400 focus:outline-none focus:border-slate-300"
            data-testid="dev-bypass-input"
          />
        </div>
      </div>
    </motion.div>
  );
}
